import { projectCreateRequestSchema, projectManifestSchema, projectOptionRequestSchema, projectPageSchema, projectSaveRequestSchema, projectViewSchema, designDocumentSnapshotSchema, storefrontAccountProfileSchema, workspaceFilePageSchema, workspaceFileUploadResponseSchema, workspaceHistoryPageSchema, projectRequestPageSchema, customerPresentationSchema } from '../../../core/src/schema/workspace';
import type { DesignDocumentSnapshot, ProjectCreateRequest, ProjectManifest, ProjectOptionRequest, ProjectPage, ProjectSaveRequest, ProjectView, WorkspaceFile, WorkspaceFilePage, WorkspaceHistoryPage, ProjectRequestPage, CustomerPresentationProjection } from '../../../core/src/schema/workspace';
import { persistedSolarDesignSchema, type PersistedSolarDesign } from '../../../core/src/schema/design-v2';

export type WorkspacePage = ProjectPage;
export type WorkspaceView = ProjectView;
export type WorkspaceManifest = ProjectManifest;
export type WorkspaceRequestPage = ProjectRequestPage;
export type WorkspaceCustomerPresentation = CustomerPresentationProjection;
export type SolarDesignDocument = PersistedSolarDesign;
type VersionedDesignSnapshot = Omit<DesignDocumentSnapshot, 'document'> & { document: PersistedSolarDesign };
const versionedDesignSnapshotSchema = designDocumentSnapshotSchema.extend({ document: persistedSolarDesignSchema });
const versionedOptionRequestSchema = projectOptionRequestSchema.extend({ newDesign: persistedSolarDesignSchema });
type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
type Parser<T> = { parse(input: unknown): T };

export type WorkspaceFailureKind = 'guest' | 'locked' | 'conflict' | 'invalid' | 'too-large' | 'file-rejected' | 'service';
export class WorkspaceFailure extends Error {
  constructor(readonly kind: WorkspaceFailureKind, message: string, readonly status?: number) { super(message); this.name = 'WorkspaceFailure'; }
}

function boundedRequest<T>(operation: (signal: AbortSignal) => Promise<T>, timeoutMs = 10_000): Promise<T> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout>;
  const deadline = new Promise<never>((_, reject) => { timer = setTimeout(() => { controller.abort(); reject(new WorkspaceFailure('service', 'The workspace service timed out.')); }, timeoutMs); });
  return Promise.race([operation(controller.signal), deadline]).finally(() => clearTimeout(timer!));
}
function localApiRoot(value: string) {
  if (!value.startsWith('/') || value.startsWith('//') || /[\\?#\u0000-\u001f]/.test(value))
    throw new WorkspaceFailure('invalid', 'The workspace API path is invalid.');
  return value.replace(/\/$/, '');
}
async function parseResponse<T>(fetcher: FetchLike, url: string, init: RequestInit, schema: Parser<T>, timeoutMs = 10_000): Promise<T> {
  let result: { status: number; ok: boolean; body: unknown };
  try { result = await boundedRequest(async signal => {
    const multipart = typeof FormData !== 'undefined' && init.body instanceof FormData;
    const response = await fetcher(url, { ...init, signal, credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json', ...(init.body && !multipart ? { 'Content-Type': 'application/json' } : {}), ...init.headers } });
    return { status: response.status, ok: response.ok, body: response.ok ? await response.json() as unknown : undefined };
  }, timeoutMs); }
  catch { throw new WorkspaceFailure('service', 'The workspace service could not be reached.'); }
  if (!result.ok) {
    throw failureForStatus(result.status);
  }
  try { return schema.parse(result.body); }
  catch { throw new WorkspaceFailure('service', 'The workspace service returned an invalid response.', result.status); }
}

function failureForStatus(status: number): WorkspaceFailure {
  const kind: WorkspaceFailureKind = status === 401 ? 'guest' : status === 403 ? 'locked' : status === 409 ? 'conflict' : status === 413 ? 'too-large' : status === 422 ? 'file-rejected' : status === 400 ? 'invalid' : 'service';
  const message = kind === 'conflict' ? 'This workspace changed elsewhere. Reload it before trying again.'
    : kind === 'guest' ? 'Sign in to continue.' : kind === 'locked' ? 'You do not have access to this workspace.'
      : kind === 'too-large' ? 'This file is larger than the upload limit.'
        : kind === 'file-rejected' ? 'This file could not be accepted. Check its type and scan status.'
          : kind === 'invalid' ? 'The workspace details need review.' : 'The workspace service is temporarily unavailable.';
  return new WorkspaceFailure(kind, message, status);
}

function validatePageRequest(page: number, size: number): void {
  if (!Number.isSafeInteger(page) || page < 0 || !Number.isSafeInteger(size) || size < 1 || size > 96 || page * size > 2_147_483_647)
    throw new WorkspaceFailure('invalid', 'The requested page is invalid.');
}

function validUploadName(value: string): boolean {
  return !!value && !!value.trim() && value.length <= 255 && !/[\u0000-\u001f/\\]/.test(value);
}

async function downloadResponse(fetcher: FetchLike, url: string): Promise<{ blob: Blob; filename: string; mimeType: WorkspaceFile['mimeType']; sizeBytes: number }> {
  let response: Response;
  try {
    response = await boundedRequest(async signal => fetcher(url, { method: 'GET', signal, credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/pdf, image/jpeg, image/png' } }), 30_000);
  } catch { throw new WorkspaceFailure('service', 'The file could not be downloaded.'); }
  if (!response.ok) throw failureForStatus(response.status);
  const mimeType = response.headers.get('content-type')?.split(';', 1)[0]?.trim().toLowerCase();
  if (mimeType !== 'application/pdf' && mimeType !== 'image/jpeg' && mimeType !== 'image/png') {
    void response.body?.cancel().catch(() => undefined);
    throw new WorkspaceFailure('service', 'The file response had an unsupported format.');
  }
  const maxBytes = 25 * 1024 * 1024;
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout>;
  const deadline = new Promise<never>((_, reject) => { timer = setTimeout(() => { controller.abort(); reject(new WorkspaceFailure('service', 'The file download timed out.')); }, 30_000); });
  try {
    const bytes = await Promise.race([readBoundedBytes(response.body, maxBytes, controller.signal), deadline]);
    const disposition = response.headers.get('content-disposition') ?? '';
    const encodedName = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
    const plainName = /filename="?([^";]+)"?/i.exec(disposition)?.[1];
    let filename = 'download';
    try { filename = encodedName ? decodeURIComponent(encodedName) : plainName ?? filename; } catch { throw new WorkspaceFailure('service', 'The file response had invalid metadata.'); }
    if (!filename || /[\u0000-\u001f\u007f/\\]/.test(filename)) throw new WorkspaceFailure('service', 'The file response had invalid metadata.');
    const exactBuffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
    return { blob: new Blob([exactBuffer], { type: mimeType }), filename, mimeType, sizeBytes: bytes.byteLength };
  } catch (error) {
    void response.body?.cancel().catch(() => undefined);
    if (error instanceof WorkspaceFailure) throw error;
    throw new WorkspaceFailure('service', 'The file could not be downloaded.');
  } finally { clearTimeout(timer!); }
}

async function readBoundedBytes(body: ReadableStream<Uint8Array> | null, maxBytes: number, signal: AbortSignal): Promise<Uint8Array> {
  if (!body) return new Uint8Array();
  const reader = body.getReader(); const chunks: Uint8Array[] = []; let size = 0;
  try {
    while (true) {
      if (signal.aborted) throw new WorkspaceFailure('service', 'The file download timed out.');
      let removeAbort: () => void = () => {};
      const aborted = new Promise<never>((_, reject) => {
        const onAbort = () => reject(new WorkspaceFailure('service', 'The file download timed out.'));
        signal.addEventListener('abort', onAbort, { once: true });
        removeAbort = () => signal.removeEventListener('abort', onAbort);
      });
      const next = await Promise.race([reader.read(), aborted]).finally(() => removeAbort());
      const { done, value } = next; if (done) break;
      size += value.byteLength; if (size > maxBytes) throw new WorkspaceFailure('too-large', 'The downloaded file exceeds its limit.');
      chunks.push(value);
    }
  } catch (error) { void reader.cancel().catch(() => undefined); throw error; }
  const result = new Uint8Array(size); let offset = 0; for (const chunk of chunks) { result.set(chunk, offset); offset += chunk.byteLength; }
  return result;
}

export type AccountProbe = { state: 'guest' } | { state: 'signed-in'; profile: Record<string, unknown> };
export async function probeStorefrontAccount(fetcher: FetchLike = fetch, apiRoot = '/api/storefront'): Promise<AccountProbe> {
  const root = localApiRoot(apiRoot);
  let result: { status: number; ok: boolean; value: unknown };
  try { result = await boundedRequest(async signal => {
    const response = await fetcher(`${root}/account/me`, { signal, credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
    return { status: response.status, ok: response.ok, value: response.ok ? await response.json() as unknown : undefined };
  }); }
  catch { throw new WorkspaceFailure('service', 'Your account could not be checked.'); }
  if (!result.ok) throw new WorkspaceFailure(result.status === 401 ? 'guest' : result.status === 403 ? 'locked' : 'service', 'Your account could not be checked.', result.status);
  if (result.value === null) return { state: 'guest' };
  const profile = storefrontAccountProfileSchema.safeParse(result.value);
  if (!profile.success) throw new WorkspaceFailure('service', 'Your account returned an invalid profile.');
  return { state: 'signed-in', profile: profile.data };
}

export class WorkspaceApi {
  private readonly apiRoot: string;
  constructor(apiRoot = '/api/storefront/extensions', private readonly fetcher: FetchLike = fetch) {
    this.apiRoot = localApiRoot(apiRoot);
  }
  private get root() { return `${this.apiRoot}/solar/v2/projects`; }
  async list(q: string, page: number, size = 24) {
    const params = new URLSearchParams({ page: String(page), size: String(size) }); if (q.trim()) params.set('q', q.trim());
    const result = await parseResponse(this.fetcher, `${this.root}/?${params}`, { method: 'GET' }, projectPageSchema);
    if (result.page !== page || result.size !== size) throw new WorkspaceFailure('service', 'The workspace service returned a different page than requested.');
    return result;
  }
  async read(projectId: string) {
    const result = await parseResponse(this.fetcher, `${this.root}/${encodeURIComponent(projectId)}`, { method: 'GET' }, projectViewSchema);
    if (result.projectId !== projectId) throw new WorkspaceFailure('service', 'The workspace identity did not match the requested project.'); return result;
  }
  async requests(projectId: string, page = 0, size = 24): Promise<ProjectRequestPage> {
    if (!Number.isSafeInteger(page) || page < 0 || !Number.isSafeInteger(size) || size < 1 || size > 100 || page * size > 2_147_483_647)
      throw new WorkspaceFailure('invalid', 'The requested page is invalid.');
    const result = await parseResponse(this.fetcher, `${this.root}/${encodeURIComponent(projectId)}/requests?page=${page}&size=${size}`, { method: 'GET' }, projectRequestPageSchema);
    if (result.projectId !== projectId || result.page !== page || result.size !== size)
      throw new WorkspaceFailure('service', 'The request page did not match the selected project or page.');
    return result;
  }
  async presentation(projectId: string, designId: string, revision: string): Promise<CustomerPresentationProjection> {
    if (!/^[1-9]\d*$/.test(revision)) throw new WorkspaceFailure('invalid', 'The saved design revision is invalid.');
    const params = new URLSearchParams({ designId, revision });
    const result = await parseResponse(this.fetcher, `${this.root}/${encodeURIComponent(projectId)}/presentation?${params}`, { method: 'GET' }, customerPresentationSchema);
    if (result.projectId !== projectId || result.designId !== designId || result.revision !== revision || result.request?.workspaceId && result.request.workspaceId !== projectId)
      throw new WorkspaceFailure('service', 'The presentation did not match the selected saved design.');
    return result;
  }
  async create(payload: ProjectCreateRequest) {
    const body = projectCreateRequestSchema.parse(payload);
    const result = await parseResponse(this.fetcher, `${this.root}/`, { method: 'POST', body: JSON.stringify(body) }, projectViewSchema);
    if (body.projectId && result.projectId !== body.projectId) throw new WorkspaceFailure('service', 'The workspace identity did not match the requested project.'); return result;
  }
  async save(projectId: string, payload: ProjectSaveRequest) {
    const body = projectSaveRequestSchema.parse(payload);
    const result = await parseResponse(this.fetcher, `${this.root}/${encodeURIComponent(projectId)}`, { method: 'PUT', body: JSON.stringify(body) }, projectViewSchema);
    if (result.projectId !== projectId) throw new WorkspaceFailure('service', 'The workspace identity did not match the requested project.'); return result;
  }
  async addOption(projectId: string, payload: Omit<ProjectOptionRequest, 'newDesign'> & { newDesign: SolarDesignDocument }) {
    const body = versionedOptionRequestSchema.parse(payload);
    const result = await parseResponse(this.fetcher, `${this.root}/${encodeURIComponent(projectId)}/options`, { method: 'POST', body: JSON.stringify(body) }, projectViewSchema);
    if (result.projectId !== projectId) throw new WorkspaceFailure('service', 'The workspace identity did not match the requested project.'); return result;
  }
  async readDesign(designId: string): Promise<{ projectId: string; revision: string; document: SolarDesignDocument }> {
    const result = await parseResponse(this.fetcher, `${this.apiRoot}/solar/designs/${encodeURIComponent(designId)}`, { method: 'GET' }, versionedDesignSnapshotSchema);
    if (result.projectId !== designId) throw new WorkspaceFailure('service', 'The design identity did not match the requested option.');
    return { projectId: result.projectId, revision: result.revision, document: result.document };
  }
  async files(projectId: string, page = 0, size = 24): Promise<WorkspaceFilePage> {
    validatePageRequest(page, size);
    const result = await parseResponse(this.fetcher, `${this.root}/${encodeURIComponent(projectId)}/files?page=${page}&size=${size}`, { method: 'GET' }, workspaceFilePageSchema);
    if (result.projectId !== projectId || result.page !== page || result.size !== size) throw new WorkspaceFailure('service', 'The file page did not match the requested workspace or page.');
    return result;
  }
  async uploadFile(projectId: string, input: { file: Blob; filename: string; title?: string; mutationId: string }): Promise<WorkspaceFile> {
    if (!(input.file instanceof Blob) || !validUploadName(input.filename) || !input.mutationId || input.mutationId.length > 200
      || input.mutationId.trim() !== input.mutationId || /[\u0000-\u001f]/.test(input.mutationId)
      || (input.title !== undefined && (input.title.length > 200 || /[\u0000-\u001f]/.test(input.title))))
      throw new WorkspaceFailure('invalid', 'The selected file details are invalid.');
    if (input.file.size > 25 * 1024 * 1024) throw new WorkspaceFailure('too-large', 'This file is larger than the upload limit.');
    const form = new FormData(); form.append('file', input.file, input.filename); form.append('mutationId', input.mutationId); if (input.title !== undefined) form.append('title', input.title);
    const result = await parseResponse(this.fetcher, `${this.root}/${encodeURIComponent(projectId)}/files`, { method: 'POST', body: form }, workspaceFileUploadResponseSchema, 75_000);
    if (result.projectId !== projectId || result.file.sizeBytes > 25 * 1024 * 1024) throw new WorkspaceFailure('service', 'The uploaded file response did not match the request.');
    return result.file;
  }
  async downloadFile(projectId: string, attachmentId: string) {
    return downloadResponse(this.fetcher, `${this.root}/${encodeURIComponent(projectId)}/files/${encodeURIComponent(attachmentId)}/content`);
  }
  async history(projectId: string, page = 0, size = 24, designId?: string): Promise<WorkspaceHistoryPage> {
    validatePageRequest(page, size);
    const params = new URLSearchParams({ page: String(page), size: String(size) }); if (designId !== undefined) params.set('designId', designId);
    const result = await parseResponse(this.fetcher, `${this.root}/${encodeURIComponent(projectId)}/history?${params}`, { method: 'GET' }, workspaceHistoryPageSchema);
    if (result.projectId !== projectId || result.designId !== (designId ?? null) || result.page !== page || result.size !== size)
      throw new WorkspaceFailure('service', 'The history page did not match the requested workspace, design or page.');
    return result;
  }
  async readRevision(projectId: string, revision: string): Promise<WorkspaceView> {
    if (!/^[1-9]\d*$/.test(revision)) throw new WorkspaceFailure('invalid', 'The requested revision is invalid.');
    const result = await parseResponse(this.fetcher, `${this.root}/${encodeURIComponent(projectId)}/revisions/${encodeURIComponent(revision)}`, { method: 'GET' }, projectViewSchema);
    if (result.projectId !== projectId || result.revision !== revision) throw new WorkspaceFailure('service', 'The workspace revision did not match the requested identity.');
    return result;
  }
  async readDesignRevision(designId: string, revision: string): Promise<VersionedDesignSnapshot> {
    if (!/^[1-9]\d*$/.test(revision)) throw new WorkspaceFailure('invalid', 'The requested revision is invalid.');
    const result = await parseResponse(this.fetcher, `${this.apiRoot}/solar/designs/${encodeURIComponent(designId)}/revisions/${encodeURIComponent(revision)}`, { method: 'GET' }, versionedDesignSnapshotSchema);
    if (result.projectId !== designId || result.revision !== revision) throw new WorkspaceFailure('service', 'The design revision did not match the requested identity.');
    return result;
  }
}

/** Reuses one id for identical retries; a changed payload represents a new mutation intent. */
export class MutationIntentCache {
  private readonly last = new Map<string, { fingerprint: string; id: string }>();
  constructor(private readonly makeId: () => string = () => globalThis.crypto?.randomUUID?.() ?? `mutation-${Date.now()}-${Math.random().toString(36).slice(2)}`) {}
  id(operation: string, payload: unknown): string {
    const fingerprint = JSON.stringify(payload);
    const current = this.last.get(operation);
    if (current?.fingerprint === fingerprint) return current.id;
    const id = this.makeId(); this.last.set(operation, { fingerprint, id }); return id;
  }
  acknowledge(operation: string, payload: unknown): void {
    const current = this.last.get(operation);
    if (current?.fingerprint === JSON.stringify(payload)) this.last.delete(operation);
  }
}
